import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import * as Sentry from '@sentry/vue'
import { authService } from '@/services/auth.service'
import { uploadFileToS3 } from '@/services/tour.service'
import type {
  User,
  LoginCredentials,
  RegisterCredentials,
  MessageResponse,
  UserProfileUpdate,
  ChangePasswordRequest,
  SetPasswordRequest,
  AvatarUploadUrlRequest,
} from '@/types/auth'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)
  const creditsBalance = computed(() => user.value?.credits_balance ?? 0)

  // Actions
  /**
   * Sign the user in from any source — password login, the Google callback,
   * the verify-email link. One place writes the store, localStorage and Sentry
   * so `isAuthenticated` flips immediately and survives a reload.
   */
  function setSession(access: string, refresh: string, userData: User) {
    accessToken.value = access
    refreshToken.value = refresh
    user.value = userData
    localStorage.setItem('accessToken', access)
    localStorage.setItem('refreshToken', refresh)
    localStorage.setItem('user', JSON.stringify(userData))
    Sentry.setUser({ id: String(userData.id), email: userData.email })
  }

  async function login(credentials: LoginCredentials) {
    try {
      const response = await authService.login(credentials)
      setSession(response.access_token, response.refresh_token, response.user)
      return response
    } catch (error) {
      logout()
      throw error
    }
  }

  async function register(credentials: RegisterCredentials): Promise<MessageResponse> {
    const response = await authService.register(credentials)
    return response
  }

  function logout() {
    user.value = null
    accessToken.value = null
    refreshToken.value = null

    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')

    Sentry.setUser(null)
  }

  async function loadUserFromStorage() {
    const storedAccessToken = localStorage.getItem('accessToken')
    const storedRefreshToken = localStorage.getItem('refreshToken')
    const storedUser = localStorage.getItem('user')

    if (storedAccessToken && storedRefreshToken && storedUser) {
      accessToken.value = storedAccessToken
      refreshToken.value = storedRefreshToken
      user.value = JSON.parse(storedUser)

      // Verify token is still valid by fetching current user
      try {
        const currentUser = await authService.getCurrentUser()
        user.value = currentUser
        localStorage.setItem('user', JSON.stringify(currentUser))
        Sentry.setUser({ id: String(currentUser.id), email: currentUser.email })
      } catch (error) {
        // Token invalid, logout
        logout()
      }
    }
  }

  function setTokens(access: string, refresh: string) {
    accessToken.value = access
    refreshToken.value = refresh
    // The axios interceptor reads the token from localStorage, not the store
    localStorage.setItem('accessToken', access)
    localStorage.setItem('refreshToken', refresh)
  }

  function setUser(userData: User) {
    user.value = userData
    localStorage.setItem('user', JSON.stringify(userData))
  }

  async function updateProfile(data: UserProfileUpdate) {
    const updated = await authService.updateProfile(data)
    setUser(updated)
    return updated
  }

  async function uploadAvatar(file: File) {
    const contentType = file.type as AvatarUploadUrlRequest['content_type']
    const { upload_url, s3_key } = await authService.requestAvatarUploadUrl({
      filename: file.name,
      content_type: contentType,
      file_size: file.size,
    })
    await uploadFileToS3(upload_url, file)
    const updated = await authService.confirmAvatar(s3_key)
    setUser(updated)
    return updated
  }

  async function deleteAvatar() {
    const updated = await authService.deleteAvatar()
    setUser(updated)
    return updated
  }

  async function changePassword(data: ChangePasswordRequest): Promise<MessageResponse> {
    return authService.changePassword(data)
  }

  async function setPassword(data: SetPasswordRequest): Promise<MessageResponse> {
    const response = await authService.setPassword(data)
    // Refresh user so `has_password` flips to true
    const refreshed = await authService.getCurrentUser()
    setUser(refreshed)
    return response
  }

  async function disconnectGoogle() {
    const updated = await authService.disconnectGoogle()
    setUser(updated)
    return updated
  }

  async function deleteAccount(): Promise<MessageResponse> {
    const response = await authService.deleteAccount()
    logout()
    return response
  }

  async function refreshQuota() {
    if (!user.value) return
    try {
      const fresh = await authService.getCurrentUser()
      setUser(fresh)
    } catch {
      // non-critical — leave the cached balance until the next call succeeds
    }
  }

  return {
    user,
    accessToken,
    refreshToken,
    isAuthenticated,
    creditsBalance,
    login,
    register,
    logout,
    loadUserFromStorage,
    setSession,
    setTokens,
    setUser,
    updateProfile,
    uploadAvatar,
    deleteAvatar,
    changePassword,
    setPassword,
    disconnectGoogle,
    deleteAccount,
    refreshQuota
  }
})
